// ============================================
// VER-COMO — impersonacion segura para direccion (2026-07-04)
// ============================================
// Flujo:
//   1. iniciarVerComo(usuario): pide el token de impersonacion al backend,
//      respalda el token/usuario ORIGINALES del director, instala el token
//      del objetivo y recarga la app completa (App.js re-bootea con
//      /api/auth/verificar y el contexto/permisos cargan como el objetivo).
//   2. salirVerComo(motivo): avisa el fin al backend (mejor esfuerzo),
//      restaura token/usuario originales y recarga.
//   3. Expiracion (30 min): el BannerVerComo vigila `expira` y sale solo;
//      ademas manejarSesionExpirada (utils/sesion.js) restaura la sesion
//      original si un 401 llega antes — el director NUNCA acaba deslogueado.
//
// Decision de diseno: el respaldo vive en localStorage y no "solo en memoria"
// porque una recarga o una pestana nueva perderian la memoria y dejarian al
// director atrapado como el objetivo hasta la expiracion. El perfil de
// seguridad es identico al del token normal, que ya vive en localStorage.

import { STORAGE_KEYS, storage, authHeaders } from './storage';
import { API_BASE } from '../config/api';

const API = API_BASE;   // direccion del backend: config/api.js (fuente unica)

// Flag ephemera para avisar "la vista Ver como termino" tras restaurar.
const FLAG_VER_COMO_TERMINO = 'beep_ver_como_termino';

// Metadatos del modo activo o null.
export function metaVerComo() {
  const m = storage.get(STORAGE_KEYS.VER_COMO_META);
  return (m && typeof m === 'object' && m.objetivo) ? m : null;
}

export function hayVerComoActivo() {
  return !!metaVerComo();
}

// Guarda anti doble-clic: un solo inicio en vuelo por pestana (veto
// logica-negocio: el doble clic duplicaba tokens y filas de auditoria).
let inicioEnCurso = false;

// Inicia el modo. Devuelve { ok } o { ok:false, error } (el caller muestra el error).
export async function iniciarVerComo(usuarioObjetivo) {
  if (inicioEnCurso) return { ok: false, error: 'Ya se está iniciando Ver como…' };
  inicioEnCurso = true;
  try {
    // Nunca encadenar: si ya hay un modo activo, salir primero.
    if (hayVerComoActivo()) {
      return { ok: false, error: 'Ya estás en modo Ver como. Sal de la vista actual primero.' };
    }
    const r = await fetch(`${API}/api/usuarios/${usuarioObjetivo.id}/ver-como`, {
      method: 'POST',
      headers: { ...authHeaders() },
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok || !data.ok || !data.token) {
      return { ok: false, error: (data && data.error) || 'No se pudo iniciar Ver como' };
    }
    // Respaldar la sesion ORIGINAL del director.
    const backup = {
      token: storage.getRaw(STORAGE_KEYS.AUTH_TOKEN),
      usuario: storage.get(STORAGE_KEYS.USER_DATA),
    };
    storage.set(STORAGE_KEYS.VER_COMO_BACKUP, backup);
    storage.set(STORAGE_KEYS.VER_COMO_META, {
      objetivo: data.objetivo,
      actor: data.actor,
      expira: Date.now() + (data.expira_en || 1800) * 1000,
    });
    // Instalar la sesion del OBJETIVO y re-bootear limpio desde el inicio.
    storage.set(STORAGE_KEYS.AUTH_TOKEN, data.token);
    storage.set(STORAGE_KEYS.USER_DATA, {
      id: data.objetivo.id, correo: data.objetivo.correo,
      nombre: data.objetivo.nombre, rol: data.objetivo.rol,
    });
    storage.remove(STORAGE_KEYS.USER_CONTEXT_CACHE);
    storage.remove(STORAGE_KEYS.DASHBOARD_ACTIVE_TAB);
    window.location.href = '/';
    return { ok: true };
  } catch (e) {
    console.error('[iniciarVerComo]', e);
    return { ok: false, error: 'Error de red iniciando Ver como' };
  } finally {
    // Si el inicio fallo, liberar la guarda (si tuvo exito la pagina recarga
    // y el modulo se reinicia de todas formas).
    inicioEnCurso = false;
  }
}

// Sale del modo y restaura la sesion original. La restauracion SIEMPRE corre:
// el aviso de fin al backend es mejor esfuerzo CON TIMEOUT (4s) — una red
// colgada jamas puede dejar al director atrapado en la vista (veto de
// logica-negocio, ciclo 1).
export async function salirVerComo(motivo) {
  const backup = storage.get(STORAGE_KEYS.VER_COMO_BACKUP);
  const meta = metaVerComo();
  const conTimeout = (ms) => {
    const ctrl = new AbortController();
    setTimeout(() => ctrl.abort(), ms);
    return ctrl.signal;
  };
  try {
    const tokenImp = storage.getRaw(STORAGE_KEYS.AUTH_TOKEN);
    const r = await fetch(`${API}/api/usuarios/ver-como/fin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tokenImp}` },
      signal: conTimeout(4000),
    });
    if (!r.ok && backup && backup.token && meta) {
      await fetch(`${API}/api/usuarios/ver-como/fin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${backup.token}` },
        body: JSON.stringify({ objetivo_id: meta.objetivo.id }),
        signal: conTimeout(4000),
      }).catch(() => {});
    }
  } catch (_) { /* mejor esfuerzo: la restauracion no depende de esto */ }

  restaurarSesionOriginal(motivo);
}

// Restaura la sesion del director SIN llamar al backend. La usa salirVerComo
// y tambien manejarSesionExpirada (utils/sesion.js) cuando el token de
// impersonacion muere con un 401 a media navegacion.
export function restaurarSesionOriginal(motivo) {
  const backup = storage.get(STORAGE_KEYS.VER_COMO_BACKUP);
  if (backup && backup.token) {
    storage.set(STORAGE_KEYS.AUTH_TOKEN, backup.token);
    if (backup.usuario) storage.set(STORAGE_KEYS.USER_DATA, backup.usuario);
  } else {
    // Sin respaldo no hay nada que restaurar (no deberia pasar): limpiar para
    // que el flujo normal de sesion expirada mande al login.
    storage.remove(STORAGE_KEYS.AUTH_TOKEN);
    storage.remove(STORAGE_KEYS.USER_DATA);
  }
  storage.remove(STORAGE_KEYS.VER_COMO_BACKUP);
  storage.remove(STORAGE_KEYS.VER_COMO_META);
  storage.remove(STORAGE_KEYS.USER_CONTEXT_CACHE);
  storage.remove(STORAGE_KEYS.DASHBOARD_ACTIVE_TAB);
  if (motivo === 'expiro') {
    try { sessionStorage.setItem(FLAG_VER_COMO_TERMINO, '1'); } catch (_) {}
  }
  window.location.href = '/';
}

// Lee y limpia el flag de "la vista termino por expiracion" (para el toast).
export function consumirFlagVerComoTermino() {
  try {
    const v = sessionStorage.getItem(FLAG_VER_COMO_TERMINO);
    if (v) sessionStorage.removeItem(FLAG_VER_COMO_TERMINO);
    return v === '1';
  } catch (_) { return false; }
}
